import type { TrackingRequest, TrackingResponse } from "./protocol";
import type {
  IntervalResult,
  PreparedTrackingFrames,
  TrackingInterval,
  TrackingOptions,
  TrackingStage,
} from "../contracts";
import { DEFAULT_TRACKING_OPTIONS } from "../options";

export class TrackingClient {
  private worker: Worker;
  private sequence = 0;
  private disposed = false;
  private terminalError: Error | null = null;
  private pending = new Map<
    number,
    {
      resolve: (value: IntervalResult) => void;
      reject: (error: Error) => void;
      preview?: (result: IntervalResult) => void;
      progress?: (stage: TrackingStage) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  >();

  constructor(url: string, options: TrackingOptions = DEFAULT_TRACKING_OPTIONS) {
    // Next/Turbopack may run this module from a blob worker URL, which cannot
    // resolve /models/... or relative media paths. Resolve on the document side.
    const absolute = (value: string) => new URL(value, document.baseURI).href;
    const sourceUrl = absolute(url);
    const workerOptions: TrackingOptions = {
      ...options,
      nanoModelUrls: {
        backbone: absolute(options.nanoModelUrls.backbone),
        head: absolute(options.nanoModelUrls.head),
        wasm: absolute(options.nanoModelUrls.wasm),
      },
    };
    this.worker = new Worker(new URL("./tracking-worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = ({ data }: MessageEvent<TrackingResponse>) => {
      if (data.type === "error" && data.id === -1) {
        this.dispose(new Error(data.message));
        return;
      }
      const request = this.pending.get(data.id);
      if (!request) return;
      if (data.type === "preview") request.preview?.(data.result);
      else if (data.type === "progress") request.progress?.(data.stage);
      else {
        clearTimeout(request.timeout);
        this.pending.delete(data.id);
        if (data.type === "result") request.resolve(data.result);
        else request.reject(new Error(data.message));
      }
    };
    this.worker.onerror = (event) => this.dispose(new Error(event.message || "Tracking worker failed"));
    this.worker.onmessageerror = () => this.dispose(new Error("Invalid tracking worker message"));
    this.send({ type: "init", url: sourceUrl, options: workerOptions });
  }

  process(
    interval: TrackingInterval,
    preview?: (result: IntervalResult) => void,
    progress?: (stage: TrackingStage) => void,
    prepared?: PreparedTrackingFrames,
  ): Promise<IntervalResult> {
    if (this.disposed) return Promise.reject(this.terminalError ?? new Error("Tracking client is disposed"));
    const id = ++this.sequence;
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => this.dispose(new Error("Tracking interval exceeded the 120-second limit")),
        120_000,
      );
      this.pending.set(id, { resolve, reject, preview, progress, timeout });
      this.send(
        { type: "process", id, interval, ...(prepared ? { prepared } : {}) },
        prepared?.decoded.frames.map((frame) => frame.data.buffer as ArrayBuffer),
      );
    });
  }

  get isDisposed() {
    return this.disposed;
  }

  private send(message: TrackingRequest, transfer?: Transferable[]) {
    try {
      if (transfer) this.worker.postMessage(message, transfer);
      else this.worker.postMessage(message);
    } catch (error) {
      this.dispose(error instanceof Error ? error : new Error(String(error)));
    }
  }

  dispose(error = new Error("Tracking cancelled")) {
    if (this.disposed) return;
    this.disposed = true;
    this.terminalError = error;
    this.worker.terminate();
    for (const request of this.pending.values()) {
      clearTimeout(request.timeout);
      request.reject(error);
    }
    this.pending.clear();
  }
}
